import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    CalendarDays,
    FileOutput,
    Paperclip,
    ShieldAlert,
    TagsIcon,
    Users,
    Check,
} from 'lucide-react';
import FileDialog from '../../files/FileDialog';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { EVENT_KEYS, trackClick } from '../../../services/api/trackingService';
import { cn } from '../../../lib/utils';
import { PolicyConditionalsView } from '../conditionals/PolicyConditionalsView';
import {
    DocumentSchema,
    InputNode,
    SectionNode,
    ViewNode,
} from '../../documents/types';
import {
    isEmptyValue,
    formatDate,
    evaluateCondition,
    resolveFixedOptionLabel,
} from '../utils/documentRendererUtils';
import { PreviewRichText } from '@nordinsight/implant-core/ui';
import { PolicyPreviewDialog } from './PolicyPreviewDialog';
export interface PolicyDocumentMeta {
    title?: string;
    status?: string;
    effectiveDate?: string;
    expiryDate?: string;
    tags?: string[];
    authors?: string[];
    attachments?: string[];
    implantTypes?: string[];
}

interface PolicyDocumentViewProps {
    schema: DocumentSchema;
    data: Record<string, any>;
    meta: PolicyDocumentMeta;
    hideToolbar?: boolean;
    className?: string;
}

const STATUS_STYLES: Record<string, string> = {
    draft: 'border-sky-400/40 bg-sky-500/10 text-sky-300',
    completed: 'border-emerald-400/40 bg-emerald-500/10 text-emerald-300',
    pending: 'border-amber-400/40 bg-amber-500/10 text-amber-300',
};

function formatInputValue(field: InputNode, value: any): React.ReactNode {
    if (isEmptyValue(value)) return null;

    if (
        (field.inputType === 'richtext' ||
            field.inputType === 'richtext-enhanced') &&
        typeof value === 'string'
    )
        return <PreviewRichText html={value} />;

    if (field.inputType === 'date') return formatDate(String(value));

    if (field.inputType === 'select' || field.inputType === 'radio')
        return resolveFixedOptionLabel(field, value);

    if (field.inputType === 'tag-list' && Array.isArray(value)) {
        const fixed = field.data?.options?.fixed;
        return (
            <ul className="list-disc pl-5 space-y-0.5">
                {value.map((v: string) => {
                    const label = fixed?.find((o) => o.value === v)?.label ?? v;
                    return (
                        <li key={v} className="text-foreground/90">
                            {label}
                        </li>
                    );
                })}
            </ul>
        );
    }

    if (Array.isArray(value)) {
        if (value.length > 0 && typeof value[0] !== 'object')
            return value.join(', ');
        return value
            .map((item) =>
                typeof item === 'object'
                    ? Object.values(item)
                          .filter((v) => !isEmptyValue(v))
                          .join(' — ')
                    : String(item)
            )
            .join('\n');
    }

    if (typeof value === 'boolean') return value ? 'Yes' : 'No';

    return (
        <span className="whitespace-pre-wrap leading-relaxed">
            {String(value)}
        </span>
    );
}

function RepeaterItems({
    field,
    items,
    data,
}: {
    field: InputNode;
    items: any[];
    data: Record<string, any>;
}) {
    const children = (field.fields ?? []).filter(
        (c) => c.kind === 'input'
    ) as InputNode[];

    return (
        <div className="space-y-4">
            {items.map((item, index) => {
                const visibleChildren = children.filter((child) => {
                    if (
                        child.hidden &&
                        evaluateCondition(child.hidden, data, item)
                    )
                        return false;
                    return !isEmptyValue(item?.[child.id]);
                });
                if (!visibleChildren.length) return null;

                return (
                    <div
                        key={`${field.id}-${index}`}
                        className={cn(
                            'space-y-1',
                            index < items.length - 1 &&
                                'pb-4 border-b border-subtle/40'
                        )}
                    >
                        {visibleChildren.map((child) => (
                            <div key={child.id}>
                                <span className="text-sm text-muted-foreground">
                                    {child.title}:{' '}
                                </span>
                                <span className="text-sm text-foreground/90">
                                    {formatInputValue(child, item[child.id])}
                                </span>
                            </div>
                        ))}
                    </div>
                );
            })}
        </div>
    );
}

function ViewField({
    field,
    data,
}: {
    field: ViewNode;
    data: Record<string, any>;
}) {
    if (field.viewType !== 'total-summary') return null;

    const dependency = field.viewProps?.dependency || field.id;
    const items = Array.isArray(data[dependency]) ? data[dependency] : [];
    const sumField = field.viewProps?.sumField;
    if (!items.length || !sumField) return null;

    const total = items.reduce(
        (sum: number, item: any) => sum + (Number(item[sumField]) || 0),
        0
    );

    return (
        <div className="rounded-lg bg-muted/20 border border-subtle/60 px-4 py-3">
            <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground/50 mb-1">
                {field.title}
            </div>
            <div className="text-2xl font-semibold text-foreground">
                {total}
                {field.viewProps?.unit && (
                    <span className="text-sm font-normal text-muted-foreground ml-1">
                        {field.viewProps.unit}
                    </span>
                )}
            </div>
        </div>
    );
}

type RenderedField = {
    key: string;
    element: React.ReactNode;
    node?: InputNode;
    value?: any;
};

function collectFields(
    section: SectionNode,
    data: Record<string, any>
): RenderedField[] {
    const results: RenderedField[] = [];
    const sectionFields = Array.isArray(section.fields) ? section.fields : [];

    for (const node of sectionFields) {
        if (node.hidden && evaluateCondition(node.hidden, data)) continue;

        if (node.kind === 'action') {
            if (node.actionType === 'checkbox' && data[node.id]) {
                results.push({
                    key: node.id,
                    element: (
                        <div className="flex items-center gap-2.5">
                            <div className="flex h-5 w-5 items-center justify-center rounded border border-emerald-500/40 bg-emerald-500/10">
                                <Check className="h-3 w-3 text-emerald-400" />
                            </div>
                            <span className="text-sm text-foreground/80">
                                {node.title}
                            </span>
                        </div>
                    ),
                });
            }
            continue;
        }

        if (node.kind === 'view') {
            results.push({
                key: node.id,
                element: <ViewField field={node} data={data} />,
            });
            continue;
        }

        if (node.kind !== 'input') continue;

        const value = data[node.id];

        if (node.functionKey === 'conditionals') {
            const condValue = data[node.id];
            if (condValue && Object.keys(condValue).length > 0) {
                results.push({
                    key: node.id,
                    element: (
                        <div className="space-y-2">
                            {!section.style?.hideTitle && node.title && (
                                <h5 className="text-sm font-semibold text-foreground/70">
                                    {node.title}
                                </h5>
                            )}
                            <PolicyConditionalsView value={condValue} />
                        </div>
                    ),
                });
            }
            continue;
        }

        if (node.inputType === 'repeater') {
            const items = Array.isArray(value) ? value : [];
            if (!items.length) continue;
            results.push({
                key: node.id,
                element: (
                    <div className="space-y-2">
                        <h5 className="text-sm font-semibold text-foreground/70">
                            {node.title}
                        </h5>
                        <RepeaterItems field={node} items={items} data={data} />
                    </div>
                ),
            });
            continue;
        }

        if (isEmptyValue(value)) continue;

        results.push({ key: node.id, element: null, node, value });
    }

    return results;
}

function PolicySection({
    section,
    data,
}: {
    section: SectionNode;
    data: Record<string, any>;
}) {
    const fields = collectFields(section, data);
    if (!fields.length) return null;

    const plainValueCount = fields.filter((f) => f.element === null).length;
    const showLabels = plainValueCount > 1;

    return (
        <section className="policy-print-section">
            {!section.style?.hideTitle && (
                <h3 className="text-base font-semibold text-foreground mb-3">
                    {section.title}
                </h3>
            )}
            <div className="space-y-4">
                {fields.map((field) => {
                    if (field.element !== null) {
                        return <div key={field.key}>{field.element}</div>;
                    }

                    return (
                        <div key={field.key}>
                            {showLabels && (
                                <h5 className="text-sm font-semibold text-foreground/70 mb-1">
                                    {field.node!.title}
                                </h5>
                            )}
                            <div className="text-[15px] text-foreground/90 leading-7">
                                {formatInputValue(field.node!, field.value)}
                            </div>
                        </div>
                    );
                })}
            </div>
        </section>
    );
}

function MetaHeader({ meta }: { meta: PolicyDocumentMeta }) {
    const { t } = useTranslation();
    const title = meta.title || t('departmentPolicies.documentView.untitled');
    const hasDates = meta.effectiveDate || meta.expiryDate;
    const tags = meta.tags ?? [];
    const authors = meta.authors ?? [];
    const implantTypes = meta.implantTypes ?? [];

    return (
        <div className="pb-8 mb-8 border-b border-subtle/60">
            <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <h1 className="text-2xl font-bold text-foreground leading-tight tracking-tight print:text-black">
                    {title}
                </h1>
                {meta.status && (
                    <Badge
                        variant="outline"
                        className={cn(
                            'print:hidden',
                            'w-fit text-xs capitalize shrink-0',
                            STATUS_STYLES[meta.status]
                        )}
                    >
                        {t(
                            `departmentPolicies.documentView.status.${meta.status}`
                        )}
                    </Badge>
                )}
            </div>

            {hasDates && (
                <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-muted-foreground mb-3 print:text-zinc-700">
                    {meta.effectiveDate && (
                        <span className="flex items-center gap-1.5">
                            <CalendarDays className="w-3.5 h-3.5" />
                            {t('departmentPolicies.documentView.effective', {
                                date: formatDate(meta.effectiveDate),
                            })}
                        </span>
                    )}
                    {meta.expiryDate && (
                        <span className="flex items-center gap-1.5">
                            <CalendarDays className="w-3.5 h-3.5" />
                            {t('departmentPolicies.documentView.expires', {
                                date: formatDate(meta.expiryDate),
                            })}
                        </span>
                    )}
                </div>
            )}

            {tags.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-3 items-center print:hidden">
                    <TagsIcon className="w-4 h-4" />
                    {tags.map((tag) => (
                        <Badge
                            key={tag}
                            variant="secondary"
                            className="text-xs font-normal bg-zinc-200 text-zinc-800 border-0 hover:bg-zinc-200"
                        >
                            {tag}
                        </Badge>
                    ))}
                </div>
            )}

            {authors.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-3 items-center text-sm text-muted-foreground print:text-zinc-700">
                    <Users className="w-3.5 h-3.5" />
                    {authors.join(', ')}
                </div>
            )}

            {implantTypes.length > 0 && (
                <div className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/5 px-5 py-4">
                    <div className="flex items-start gap-3">
                        <ShieldAlert className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
                        <div>
                            <p className="text-sm font-semibold text-amber-300 mb-1">
                                {t(
                                    'departmentPolicies.documentView.applicableImplantTypes'
                                )}
                            </p>
                            <ul className="list-disc pl-5 space-y-0.5">
                                {implantTypes.map((type) => (
                                    <li
                                        key={type}
                                        className="text-sm text-foreground/90"
                                    >
                                        {type}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export function PolicyDocumentView({
    schema,
    data,
    meta,
    hideToolbar = false,
    className,
}: PolicyDocumentViewProps) {
    const { t } = useTranslation();
    const [previewOpen, setPreviewOpen] = useState(false);
    const schemaSections = Array.isArray(schema.sections)
        ? schema.sections
        : [];

    const visibleSections = schemaSections.filter((section) => {
        if (section.sectionType === 'actions') return false;
        if (section.hidden && evaluateCondition(section.hidden, data))
            return false;
        return true;
    });
    const attachments = meta.attachments ?? [];

    const hasContent = visibleSections.some(
        (s) => collectFields(s, data).length > 0
    );

    return (
        <div className={cn('flex flex-col', className)}>
            {!hideToolbar && (
                <div className="flex justify-end gap-2 mb-4 print:hidden">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                            trackClick(EVENT_KEYS.clickExport, {
                                previewSource: 'policy',
                            });
                            setPreviewOpen(true);
                        }}
                    >
                        <FileOutput className="w-4 h-4 mr-2" />
                        {t('departmentPolicies.readPage.export', 'Export')}
                    </Button>
                </div>
            )}

            {!hideToolbar && previewOpen && (
                <PolicyPreviewDialog
                    open={previewOpen}
                    onOpenChange={setPreviewOpen}
                    schema={schema}
                    data={data}
                    meta={meta}
                />
            )}

            <div>
                <MetaHeader meta={meta} />

                <div className="space-y-8">
                    {visibleSections.map((section) => (
                        <div key={section.id}>
                            <PolicySection section={section} data={data} />
                        </div>
                    ))}

                    {attachments.length > 0 && (
                        <section className="pt-2 border-t border-subtle/60 print:hidden">
                            <div className="mb-3 flex items-center gap-2 text-sm text-muted-foreground">
                                <Paperclip className="w-3.5 h-3.5" />
                                <span>
                                    {t(
                                        'departmentPolicies.documentView.attachments',
                                        { count: attachments.length }
                                    )}
                                </span>
                            </div>
                            <div className="flex flex-col gap-0.5">
                                {attachments.map((fileId) => (
                                    <FileDialog
                                        key={fileId}
                                        file_id={fileId}
                                        newTab
                                        buttonType="compact"
                                    />
                                ))}
                            </div>
                        </section>
                    )}

                    {!hasContent && (
                        <div className="flex flex-col items-center py-16 text-center">
                            <p className="text-sm text-muted-foreground">
                                {t(
                                    'departmentPolicies.documentView.emptyState'
                                )}
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
